import Cookies from 'js-cookie'

import { BLANK_CHARACTER, fantasyNames } from '../constants/characters';
import { RACE_ADJUSTMENTS, CON_ADJUSTMENTS, STR_ADJUSTMENTS, CHA_ADJUSTMENTS } from '../constants/abilityAdjustments.js';
import getSavingThrows from './getSavingThrows.js'
import { calculateStrengthBonus } from './calculateStrengthBonus.js';
import { calculateDexBonus } from './calculateDexBonus.js';
import { calculateIntBonus } from './calculateIntBonus.js';
import { calculateWisBonus } from './calculateWisBonus.js';
import { calculateChaBonus } from './calculateChaBonus.js';
import { calculateGold } from './calculateGold.js';
import { calculateToHit } from './calculateToHit.js';
import { CLASS_HIT_DICE, RACE_CLASSES, CLASS_REQUIREMENTS } from '../constants/classDefinitions.js';

// Constants for ability score generation methods
const ABILITY_SCORE_METHODS = {
  METHOD_I: '4d6_drop_lowest',
  METHOD_II: '3d6_twelve_choose_six',
  METHOD_III: '3d6_six_per_ability',
  METHOD_IV: '3d6_twelve_sets_choose_one'
};

// Weighting to make certain methods more likely for random case
const ABILITY_METHODS_WEIGHTS = {
  [ABILITY_SCORE_METHODS.METHOD_I]: 6,
  [ABILITY_SCORE_METHODS.METHOD_II]: 2,
  [ABILITY_SCORE_METHODS.METHOD_III]: 6,
  [ABILITY_SCORE_METHODS.METHOD_IV]: 1
};




// Utility functions
function rollDie(sides) {
  return Math.floor(Math.random() * sides) + 1;
}

function rollDice(num, sides, dropLowest = false) {
  const rolls = [];
  for (let i = 0; i < num; i++) {
    rolls.push(rollDie(sides));
  }
  if (dropLowest) {
    rolls.sort((a, b) => a - b);
    rolls.shift();
  }
  return rolls.reduce((sum, roll) => sum + roll, 0);
}

function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}


// ---------- MAIN FUNCTION: GENERATE CHARACTER ----------

function generateCharacter(method) {
  const character = { ...BLANK_CHARACTER };

  // Step 1 & 2: Generate ability scores
  let selectedMethod = method;
  if (method === 'random' || method === '') {
    // Randomly select a method based on weighted options
    const weightedMethods = [];
    for (const [methodKey, weight] of Object.entries(ABILITY_METHODS_WEIGHTS)) {
      for (let i = 0; i < weight; i++) {
        weightedMethods.push(methodKey);
      }
    }
    selectedMethod = weightedMethods[Math.floor(Math.random() * weightedMethods.length)];
  }

  console.log(`Ability Score Generation Method: ${selectedMethod}`);

  // after rolling, randomize scores except for method III
  let randomizeAfterRolling = true;

  let scores = [];
  if (selectedMethod === ABILITY_SCORE_METHODS.METHOD_I) {
    for (let i = 0; i < 6; i++) {
      scores.push(rollDice(4, 6, true));
    }
    character.chars['myHPnote'] = 'may move ability scores'

  } else if (selectedMethod === ABILITY_SCORE_METHODS.METHOD_II) {
    const rolls = [];
    for (let i = 0; i < 12; i++) {
      rolls.push(rollDice(3, 6));
    }
    rolls.sort((a, b) => b - a);
    scores = rolls.slice(0, 6);
    character.chars['myHPnote'] = 'may move ability scores'

  } else if (selectedMethod === ABILITY_SCORE_METHODS.METHOD_III) {
    for (let i = 0; i < 6; i++) {
      const rolls = [];
      for (let j = 0; j < 6; j++) {
        rolls.push(rollDice(3, 6));
      }
      console.log('rolls for method 3: ', rolls)
      scores.push(Math.max(...rolls));
    }
    character.chars['myHPnote'] = 'ability scores fixed'
    randomizeAfterRolling = false

  } else if (selectedMethod === ABILITY_SCORE_METHODS.METHOD_IV) {
    const sets = [];
    for (let i = 0; i < 12; i++) {
      const set = [];
      for (let j = 0; j < 6; j++) {
        set.push(rollDice(3, 6));
      }
      sets.push(set);
    }
    scores = sets.reduce((maxSet, currentSet) =>
      currentSet.reduce((sum, val) => sum + val, 0) > maxSet.reduce((sum, val) => sum + val, 0)
        ? currentSet
        : maxSet,
      sets[0]
);
    character.chars['myHPnote'] = 'ability scores fixed'
    
  } else {
    throw new Error('Invalid method');
  }
  
  // store the selected method so player can see it
  character.items['myOth1'] = `Method: ${selectedMethod.replace(/_/g, ' ')}`;

// Step 3: Assign scores to abilities
const abilities = ['myStr', 'myInt', 'myWis', 'myDex', 'myCon', 'myCha'];
if (randomizeAfterRolling)
  scores = shuffleArray(scores);
abilities.forEach((ability, index) => {
  character.stats[ability] = scores[index];
});
 
// Step 4: SELECT A CLASS

function chooseClass() {}

    // calculate allowed classes based on abilities
    let eligibleClasses = Object.keys(CLASS_REQUIREMENTS).filter(cls => {
      const reqs = CLASS_REQUIREMENTS[cls];
      return (
        character.stats.myStr >= reqs.Str &&
        character.stats.myInt >= reqs.Int &&
        character.stats.myWis >= reqs.Wis &&
        character.stats.myDex >= reqs.Dex &&
        character.stats.myCon >= reqs.Con &&
        character.stats.myCha >= reqs.Cha
      );
    });

    if (eligibleClasses.length === 0) {
      throw new Error('No eligible classes for ability scores');
    }

    console.log('eligible Classes: ', eligibleClasses)

    // weight classes based on abilities
    const PREFERRED_CLASSES = {
      myStr: ['Fighter', 'Ranger', 'Paladin'],
      myInt: ['Magic-User', 'Illusionist'],
      myWis: ['Cleric', 'Druid'],
      myDex: ['Thief', 'Assasin'],
      myCon: ['Fighter', 'Ranger', 'Paladin'],
      myCha: ['Paladin', 'Druid']
      }
    const ability_list = ['myStr', 'myInt', 'myWis', 'myDex', 'myCon', 'myCha']
    let class_weights = {};
    let best_ability = 'myStr'
    let best_ability_value = 3
    for (const abi of ability_list) {
      if (character.stats[abi] > best_ability_value) {
        best_ability = abi
        best_ability_value = character.stats[abi]
      } 
    }
    console.log('best ability: ', best_ability, ' ', best_ability_value)

    let best_ability_weight = 75
    if ( best_ability_value === 15 ) best_ability_weight = 90
    if ( best_ability_value === 16 ) best_ability_weight = 93
    if ( best_ability_value === 17 ) best_ability_weight = 96
    if ( best_ability_value >= 18 ) best_ability_weight = 99
    console.log(PREFERRED_CLASSES[best_ability])

    // assign weights to eligible classes
    let weightedClasses = {}
    for (const charClass of eligibleClasses ) {
      if (PREFERRED_CLASSES[best_ability].includes(charClass)) {
        weightedClasses[charClass] = best_ability_weight
      } else {
        weightedClasses[charClass] = 100-best_ability_weight
      }
      if (charClass === 'Assassin' )  { weightedClasses[charClass] = weightedClasses[charClass]/10 }
    }
    console.log('weighted classes: ',weightedClasses)

    /*if (character.stats.myStr >= 18) {
      weightedClasses = weightedClasses.filter(cls => ['Fighter', 'Ranger', 'Paladin'].includes(cls));
    }
*/
    // get a random class based on weightedClasses
    let randomClass
    const totalWeight = Object.values(weightedClasses).reduce((sum, weight) => sum + weight, 0);
    const random = Math.random() * totalWeight;
    let cumulativeWeight = 0;
    for (const [key, weight] of Object.entries(weightedClasses)) {
        cumulativeWeight += weight;
        if (random <= cumulativeWeight) { randomClass = key; break; }
    }
    console.log('randomClass: ', randomClass)
    //const randomClass = Object.keys(weightedClasses)[Object.keys(weightedClasses).length - 1];

  // Randomly select a class from the weighted ones
  character.chars.myClass = randomClass; // eligibleClasses[Math.floor(Math.random() * eligibleClasses.length)];

  // Step 5: Choose a race that can have this class
  const eligibleRaces = Object.keys(RACE_CLASSES).filter(race =>
    RACE_CLASSES[race].includes(character.chars.myClass)
  );
  character.chars.myRace = eligibleRaces[Math.floor(Math.random() * eligibleRaces.length)];

  // Step 6: Adjust ability scores based on race
  const adjustments = RACE_ADJUSTMENTS[character.chars.myRace];
  console.log('Race Adjustments', adjustments )
  console.log('character before', character.stats )
  for (const [ability, mod] of Object.entries(adjustments)) {
    // console.log('abilty ', ability, 'mod ', mod)
    character.stats[`my${ability}`] = Math.min(18, Math.max(3, character.stats[`my${ability}`] + mod));
  }
  console.log('character after', character.stats )


  // Step 6.5: Roll d100 for fighters with str 18
  let orcBonus = 0;
  if (character.stats.myStr >= 19) { // can't start with 19 strength
    character.stats.myStr = 18
    orcBonus = 10 }
  if (character.stats.myStr >= 18 && ['Fighter','Ranger','Paladin'].includes(character.chars.myClass)) {
    let d100Roll = rollDice(1,100) + orcBonus
    if (d100Roll >= 100) { d100Roll = 0 }
    character.stats.myStr = `18/${String(d100Roll).padStart(2, '0')}`;
  }
  // Step 7: Roll hit points based on class
  const hitDie = CLASS_HIT_DICE[character.chars.myClass];
  console.log('Hit Die:', hitDie);

  const [numDice, dieType] = hitDie.split('d').map(Number);
  let hp = 0;
  for (let i = 0; i < numDice; i++) {
    hp += rollDie(dieType);
  }

  // ----- SAVE CON BONUS & ADJUST HIT POINTS BASED ON CON -----
  let HP_adj = CON_ADJUSTMENTS[parseInt(character.stats['myCon'], 10)].hitPointAdj
  if (!["Fighter", "Paladin", "Ranger"].includes(character.chars.myClass)) 
    { HP_adj = Math.min(2, HP_adj ); }
  console.log("Con:", character.stats['myCon'], "HP_adj: ", HP_adj, "HP_before:",  hp )
  hp += HP_adj;
  character.stats['myConBon'] =
      HP_adj === 0 ? "" : 
      (HP_adj < 0 ? `-${Math.abs(HP_adj)}` : `+${HP_adj}`);

  character.chars['myHPbase'] = Math.max(1, hp).toString();
  character.chars['myLevel'] = "1";
  character.chars['myXP'] = "0";

  // ----- SAVE STR BONUS -----
  const STR_bonus = calculateStrengthBonus(character)
  const STR_bonus_string = STR_bonus.str
  console.log('strBonus', STR_bonus_string)
  character.stats.myStrBon = STR_bonus_string;

    // ----- SAVE DEXBONUS -----
  const DEX_bonus = calculateDexBonus(character)
  const DEX_bonus_string = DEX_bonus.str
  console.log('dex Bonus', DEX_bonus_string)
  character.stats.myDexBon = DEX_bonus_string;
  if (DEX_bonus_string !== "" ) {
    try {
      let DBS = DEX_bonus.dict.defense;
      character.armor.myArm3 = "DEX Bonus"
      character.armor.myAb3 = DEX_bonus.dict.defense.toString()
    } catch (error) {
      console.log(error)
    }
  } else {
      character.armor.myArm3 = ""
      character.armor.myAb3 = ""
  }

  // ----- SAVE INT BONUS -----
  const INT_bonus = calculateIntBonus(character)
  const INT_bonus_string = INT_bonus.str
  character.stats.myIntBon = INT_bonus_string;

  // ----- SAVE WIS BONUS -----
  const WIS_bonus = calculateWisBonus(character)
  const WIS_bonus_string = WIS_bonus.str
  character.stats.myWisBon = WIS_bonus_string;

  // ----- SAVE CHA BONUS -----
  const CHA_bonus = calculateChaBonus(character)
  const CHA_bonus_string = CHA_bonus.str
  character.stats.myChaBon = CHA_bonus_string;
  /*
  let lookupKey = character.stats['myCha'].toString().replace(/[^0-9]/g, ''); // strip out non-numerical
  try {
    const CHA_bonus = CHA_ADJUSTMENTS[lookupKey]
    character.stats.myChaBon = CHA_bonus.reaction_adj === 0 ? "" : `${CHA_bonus.reaction_adj}%`
  } catch(error) { console.log(error)}
*/

 // ----- STORE AS STRINGS: all ability values as strings, not numbers
 abilities.forEach((ability, index) => {
  character.stats[ability] = character.stats[ability].toString();
});

  // get saving throws
  const savingThrows = getSavingThrows(character.chars['myClass'], character.chars['myLevel'])
  character.saves['mySav1'] = savingThrows['poison'].toString();
  character.saves['mySav2'] = savingThrows['poly'].toString();
  character.saves['mySav3'] = savingThrows['wand'].toString();
  character.saves['mySav5'] = savingThrows['breath'].toString();
  character.saves['mySav4'] = savingThrows['magic'].toString();

  // ----- GOLD -----
  const startingGOLD = calculateGold(character)
  console.log('Gold: ', startingGOLD)
  character.chars.myGold = startingGOLD.toString()

  // ----- TO HIT -----
  const myToHit = calculateToHit(character)
  character.chars.myToHit = myToHit.toString()

  // ----- WEAPONS -----
  const charClass = character.chars.myClass
  if ( ['Fighter', 'Paladin', 'Ranger'].some(str => charClass.includes(str)) ) {
    character.weapons.myWeap1 = 'Longsword'
    character.weapons.myWdmg1 = "1-8/1-12"
    character.weapons.myWeap2 = 'Shortbow'
    character.weapons.myWdmg2 = "1-6"
  }
  if ( ['Cleric'].some(str => charClass.includes(str)) ) {
    character.weapons.myWeap1 = 'Mace'
    character.weapons.myWdmg1 = "2-7/1-6"
    character.weapons.myWeap2 = ""
    character.weapons.myWdmg2 = ""
  }
  if ( ['Druid'].some(str => charClass.includes(str)) ) {
  character.weapons.myWeap1 = 'Sickle'
  character.weapons.myWdmg1 = "1-8"
  character.weapons.myWeap2 = ""
  character.weapons.myWdmg2 = ""
}
  if ( ['Thief', 'Assassin'].some(str => charClass.includes(str)) ) {
    character.weapons.myWeap1 = 'Shortsword'
    character.weapons.myWdmg1 = "1-6"
    character.weapons.myWeap2 = 'Shortbow'
    character.weapons.myWdmg2 = "1-6"
  }
  if ( ['Magic-User', 'Illusionist'].some(str => charClass.includes(str)) ) {
    character.weapons.myWeap1 = 'Staff'
    character.weapons.myWdmg1 = "1-6"
    character.weapons.myWeap2 = 'Dart'
    character.weapons.myWdmg2 = "1-3"
  }
    if ( ['Monk'].some(str => charClass.includes(str)) ) {
    character.weapons.myWeap1 = 'Hand'
    character.weapons.myWdmg = "1-3"
    character.weapons.myWeap2 = ""
    character.weapons.myWdmg2 = ""    
  }

  const toHitValue = myToHit + STR_bonus.dict.toHit
  const toHitString = toHitValue === 0 ? "0" :
      (toHitValue < 0 ? `-${Math.abs(toHitValue)}` : `+${toHitValue}`);
  character.weapons.myWth1 = toHitString

  const damageValue = STR_bonus.dict.damage
  const damageString = damageValue === 0 ? "0" :
      (damageValue < 0 ? `-${Math.abs(damageValue)}` : `+${damageValue}`);
  character.weapons.myWdb1 = damageString

  if ( character.weapons.myWeap2 !== "" ) {
      const rangeToHit = myToHit + DEX_bonus.dict.reaction
      const rangeToHitString = rangeToHit === 0 ? "0" :
          (rangeToHit < 0 ? `-${Math.abs(rangeToHit)}` : `+${rangeToHit}`);
      character.weapons.myWth2 = rangeToHitString
      character.weapons.myWdb2 = "0"
  } else {
      character.weapons.myWth2 = ""
      character.weapons.myWdb2 = ""
  }

  // ----- ARMOR -----
  character.armor.myArm1 = ""
  character.armor.myAb1 = ""
  character.armor.myRear = "0"
  character.chars.myMov = '12"'

  if ( ['Fighter', 'Paladin', 'Ranger','Cleric'].some(str => charClass.includes(str)) ) {
    character.armor.myArm1 = "Chainmail"
    character.armor.myAb1 = "5"
    character.armor.myRear = "5"
    character.chars.myMov = '9"'
  }
  if ( ['Druid', 'Thief', 'Assasin'].some(str => charClass.includes(str)) ) {
    character.armor.myArm1 = "Leather Armor"
    character.armor.myAb1 = "2"
    character.armor.myRear = "2"
  }
  let myAC = 0
  if ( character.armor.myAb1 !== "" ) { myAC += parseInt(character.armor.myAb1)}
  if ( character.armor.myAb2 !== "" ) { myAC += parseInt(character.armor.myAb2)}
  if ( character.armor.myAb3 !== "" ) { myAC += parseInt(character.armor.myAb3)}
  character.chars.myAC = myAC.toString()

  // give the dude a name
  const randomName = fantasyNames[Math.floor(Math.random() * fantasyNames.length)];
  character.name = randomName;
  Cookies.set('currentChar', randomName )
  
  console.log('generated character:', character)
  return character;
}

export { generateCharacter, ABILITY_SCORE_METHODS };